import { Form, Formik, FormikProps, FormikValues } from 'formik';
import { useCallback, useEffect, useRef } from 'react';
import { SOCIAL_PLATFORM_PIPELINE_STATUS } from '~/clientApi/offPlatform.model';
import { useOPCreatorSearch } from '~/hooks/api/useOPCreatorSearch';
import { parseHandle } from '~/screens/ManagePlatforms';
import { useAddNewPlatformsStore } from '~/screens/ManagePlatforms/AddNewPlatformsModal/useAddNewPlatformsStore';
import { useEditPlatformsStore } from '~/screens/ManagePlatforms/EditPlatformsModal_DEPRECATED';
import { PlatformForm } from '~/screens/ManagePlatforms/common';
import { useGetAvailablePlatforms } from '~/screens/ManagePlatforms/hooks';

export const AddPlatformForm: React.FC = () => {
  const formRef = useRef<FormikProps<FormikValues>>(null);

  const {
    setValues,
    setIsModalOpen: closeAddNewPlatformsModal,
    setPlatform,
  } = useAddNewPlatformsStore();
  const { connectedPlatforms, openModal: openEditPlatformsModal } =
    useEditPlatformsStore();

  const searchOPCreators = useOPCreatorSearch();

  const availablePlatforms = useGetAvailablePlatforms(connectedPlatforms);

  const initialValues = {
    instagram: '',
    tiktok: '',
    snapchat: '',
    youtube: '',
  };

  useEffect(() => {
    if (!searchOPCreators.isSuccess) return;

    const { instagram, tiktok, snapchat, youtube } = formRef.current
      ?.values as FormikValues;

    const pipes = {
      instagram: searchOPCreators.data?.instagram?.pipelineStatus,
      tiktok: searchOPCreators.data?.tiktok?.pipelineStatus,
      snapchat: searchOPCreators.data?.snapchat?.pipelineStatus,
      youtube: searchOPCreators.data?.youtube?.pipelineStatus,
    };

    const isFinishedOrInvalid = (
      pipelineStatus?: SOCIAL_PLATFORM_PIPELINE_STATUS,
    ) =>
      pipelineStatus === SOCIAL_PLATFORM_PIPELINE_STATUS.finished ||
      pipelineStatus === SOCIAL_PLATFORM_PIPELINE_STATUS.invalid;

    if (
      (instagram && isFinishedOrInvalid(pipes.instagram)) ||
      (tiktok && isFinishedOrInvalid(pipes.tiktok)) ||
      (snapchat && isFinishedOrInvalid(pipes.snapchat)) ||
      (youtube && isFinishedOrInvalid(pipes.youtube))
    ) {
      setValues(formRef.current?.values as FormikValues);
      setPlatform(searchOPCreators.data);
    }
  }, [searchOPCreators, setValues, setPlatform]);

  const handleBack = useCallback(() => {
    setValues({
      instagram: '',
      tiktok: '',
      snapchat: '',
      youtube: '',
    });
    closeAddNewPlatformsModal(false);
    openEditPlatformsModal();
  }, [setValues, closeAddNewPlatformsModal, openEditPlatformsModal]);

  const handleSubmit = useCallback(
    (formValues: FormikValues) => {
      const { instagram, tiktok, snapchat, youtube } = formValues;

      const payload = {
        igHandle: parseHandle(instagram),
        ttHandle: parseHandle(tiktok),
        snapHandle: parseHandle(snapchat),
        ytHandle: parseHandle(youtube),
      };
      searchOPCreators.mutate(payload);
    },
    [searchOPCreators],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      innerRef={formRef}
      onSubmit={(values) => handleSubmit(values)}
    >
      <Form>
        <PlatformForm
          platforms={availablePlatforms}
          searchOPCreators={searchOPCreators}
          onBackClick={handleBack}
        />
      </Form>
    </Formik>
  );
};
